import { ReactNode, useCallback, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { IconCircleMinus } from '@rossum/ui/icons/tabler';
import {
  Chip,
  Popover,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { FilterOperator } from '../../../mql/operators';
import { useFetchAsyncChipValues } from '../../hooks/useFetchAsyncChipValues';
import {
  FilterColDef,
  FilterItem,
  FilterValues,
  isFilterAdvanced,
  isFilterSimple,
  ValidatedFilterItem,
} from '../../types';
import { FilterComponentProps } from '../FilterComponentProps';
import { getChipLabel, getChipTooltip } from './utils';

const areColumnsEqual = (...columns: FilterColDef[]) => {
  const [firstColumn, secondColumn] = columns.map(col =>
    Object.fromEntries(
      Object.entries(col).flatMap(([key, value]) =>
        typeof value === 'function' ? [] : [[key, value]]
      )
    )
  );
  return R.isDeepEqual(firstColumn, secondColumn);
};

export const FilterChip = ({
  onRemove,
  children,
  incomingFilterItem,
}: {
  incomingFilterItem: ValidatedFilterItem;
  onRemove: () => void;
  children: (params: {
    onClose: () => void;
    convertToAdvancedFilter: FilterComponentProps['convertToAdvancedFilter'];
    onOperatorChange: (operator: FilterOperator) => void;
    filterItem: FilterItem;
  }) => ReactNode;
}) => {
  const intl = useIntl();
  const [localFilterItem, setLocalFilterItem] = useState<FilterItem | null>(
    incomingFilterItem
  );

  const { column } = incomingFilterItem;

  const asyncChipValues = useFetchAsyncChipValues({
    field: column.field,
  });

  const filterItem = {
    ...incomingFilterItem,
    column: { ...incomingFilterItem.column, ...asyncChipValues },
  };

  if (localFilterItem && !areColumnsEqual(column, localFilterItem.column)) {
    setLocalFilterItem({ ...localFilterItem, column });
  }

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const { columnLabel, selectedOptions } = getChipLabel(filterItem);

  const onClose = () => setAnchorEl(null);

  const tooltip = incomingFilterItem.isValid
    ? getChipTooltip(filterItem)
    : intl.formatMessage({
        id: 'containers.filtering.chip.invalidTooltip',
      });

  const onOperatorChange = useCallback(
    (operator: FilterOperator, value?: unknown) => {
      if (localFilterItem && isFilterSimple(localFilterItem))
        setLocalFilterItem({
          ...localFilterItem,
          filterContext: { ...localFilterItem.filterContext, value, operator },
        });
    },
    [localFilterItem]
  );

  const convertToAdvancedFilter = (
    operator: FilterOperator,
    existingInput?: string | string[]
  ) => {
    if (localFilterItem && isFilterSimple(localFilterItem)) {
      const newFilterContext: FilterValues[] = [
        {
          operator,
          value: existingInput ?? localFilterItem.filterContext.value,
        },
      ];

      setLocalFilterItem({
        ...localFilterItem,
        filterContext: newFilterContext,
      });
    }
  };

  return (
    <>
      <Tooltip
        title={tooltip}
        placement="top"
        slotProps={{
          tooltip: {
            sx: { maxHeight: 100, overflowY: 'auto' },
          },
          arrow: { sx: { display: 'none' } },
        }}
      >
        <Chip
          color={incomingFilterItem.isValid ? 'default' : 'warning'}
          onClick={
            incomingFilterItem.isValid
              ? e => {
                  setAnchorEl(e.currentTarget);
                  setLocalFilterItem(filterItem);
                }
              : undefined
          }
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" fontWeight="bold">
                {columnLabel}
              </Typography>

              {selectedOptions ? (
                selectedOptions.map(({ operatorKey, selectedOption }) => (
                  <Stack direction="row" key={operatorKey}>
                    {operatorKey ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'secondary.light',
                          mr: 0.5,
                        }}
                      >
                        {intl.formatMessage({
                          id: `containers.filtering.operators.${operatorKey}`,
                        })}
                      </Typography>
                    ) : null}
                    {filterItem.column.areValuesLoading ? (
                      <Skeleton variant="text" width={50} />
                    ) : (
                      <Typography
                        variant="body2"
                        maxWidth={150}
                        textOverflow="ellipsis"
                        overflow="hidden"
                        fontWeight="bold"
                      >
                        {selectedOption}
                      </Typography>
                    )}
                  </Stack>
                ))
              ) : isFilterAdvanced(filterItem) ? (
                <Typography variant="body2">
                  {intl.formatMessage(
                    { id: 'containers.filtering.chip.ruleCount' },
                    { count: filterItem.filterContext.length }
                  )}
                </Typography>
              ) : null}
            </Stack>
          }
          onDelete={onRemove}
          deleteIcon={
            <SvgIcon>
              <IconCircleMinus />
            </SvgIcon>
          }
          data-cy={`all-documents-filter-chip-${filterItem.column.field}`}
        />
      </Tooltip>
      <Popover
        open={!!anchorEl}
        onClose={onClose}
        onTransitionExited={() => setLocalFilterItem(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        {children({
          onClose,
          convertToAdvancedFilter,
          filterItem: localFilterItem ?? filterItem,
          onOperatorChange,
        })}
      </Popover>
    </>
  );
};
